import { Trans } from "@lingui/react";
import { isPrivateAdvertiser } from "@mobility-item-page/utilities";
import { ElementType, Intent, TargetType } from "@mobility-item-page/utilities/client";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import { isFinn } from "src/utils/brand.ts";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";

type Props = {
  hideClaim?: boolean;
};
const ComplaintClaim = ({ hideClaim }: Props) => {
  const ad = useAdViewAd();
  const meta = useAdViewMeta();
  const { brand } = useAppConfig();

  const isPrivateAd = isPrivateAdvertiser(meta.ownerUrn);

  const complaintsUrl = "complaint_claim_url" in ad ? ad.complaint_claim_url : undefined;
  // TODO: Blocked by MIT-1191
  const disputeUrl =
    "https://hjelpesenter.finn.no/hc/no/articles/22604768543762-Reklamasjonsrett-ved-kj%C3%B8p-og-salg-av-motorvogn#h_01JZWCTVNYJEMK4BKP9TZF39K8";

  if (hideClaim || !isFinn(brand)) {
    return null;
  }

  return isPrivateAd ? (
    <ComplaintClaimPrivate complaintsUrl={complaintsUrl} disputeUrl={disputeUrl} />
  ) : (
    <ComplaintClaimDealer complaintsUrl={complaintsUrl} />
  );
};

const ComplaintClaimPrivate = ({ complaintsUrl, disputeUrl }: { complaintsUrl?: string; disputeUrl?: string }) => {
  const { trackClick } = usePulse();

  const disputeInfo = (
    <>
      <h2 className="t3 mt-16 mb-0">
        <Trans
          id="itemPageLayout.complaintClaim.mekle.title"
          message="Independent Dispute Resolution"
          comment="Title for independent dispute resolution (Finn only)"
        />
      </h2>
      <p className="mt-16 mb-0">
        <Trans
          id="itemPageLayout.complaintClaim.mekle.text"
          message="Our partner Mekle offers digital dispute resolution, where both parties in a complaint case receive assistance from a legal expert to find a fair solution."
          comment="About Mekle's offer for digital dispute resolution (Finn only)"
        />
      </p>

      <a
        className="block mt-16"
        target="_blank"
        rel="noopener nofollow"
        href={disputeUrl}
        onClick={() =>
          // Tracking event: MO_ev85
          trackClick({
            name: "Read about Mekle dispute resolution",
            objectId: "MO_ev85",
            elementType: ElementType.Link,
            intent: Intent.Open,
            objectExtras: { listingType: "private" },
            target: {
              id: "read-about-mekle-dispute-resolution",
              type: TargetType.Article,
              name: "Få hjelp av vår samarbeidspartner Mekle",
            },
          })
        }
      >
        <Trans
          id="itemPageLayout.complaintClaim.mekle.readMore"
          message="Read more about Mekle here."
          comment="Mekle read more link (Finn only)"
        />
      </a>
    </>
  );

  return (
    <section className="pt-40 mt-40 border-t">
      <h2 className="t3 mb-0">
        <Trans
          id="itemPageLayout.complaintClaim.private.title"
          message="Right of complaint when purchasing from a private person"
          comment="Private claim title"
        />
      </h2>
      <p className="mt-16 mb-0">
        <Trans
          id="itemPageLayout.complaintClaim.private.text"
          message="Normally, a 2-year complaint period applies to hidden faults and defects. The right to complain is used if the product is in a worse condition than what you had reason to expect, based on information you have received from the seller. Please note that both parties can waive the right to complain if this is clearly specified in a purchase contract."
          comment="Private claim text"
        />
      </p>
      {complaintsUrl && (
        <p className="mt-16 mb-0">
          <a
            target="_blank"
            rel="noopener nofollow"
            href={complaintsUrl}
            onClick={() =>
              // Tracking event: MO_ev20
              trackClick({
                name: "Read about claim rights",
                objectId: "MO_ev20",
                elementType: ElementType.Link,
                intent: Intent.Open,
                objectExtras: { listingType: "private" },
                target: {
                  id: "open-claim-rights-link",
                  type: TargetType.Article,
                  name: "Reklamasjonsrett ved kjøp og salg av bil",
                },
              })
            }
          >
            <Trans
              id="itemPageLayout.complaintClaim.private.readMore"
              message="Read more about the right to complain at FINN's help centre."
              comment="Private claim read more link"
            />
          </a>
        </p>
      )}
      {disputeInfo}
    </section>
  );
};

const ComplaintClaimDealer = ({ complaintsUrl }: { complaintsUrl?: string }) => {
  const { trackClick } = usePulse();
  return (
    <div className="pt-40 mt-40 border-t">
      <h2 className="t3 mb-0">
        <Trans
          id="itemPageLayout.complaintClaim.dealer.title"
          message="Complaint right when purchasing from a dealer"
          comment="Dealer claim title"
        />
      </h2>
      <p className="mt-16 mb-0">
        <Trans
          id="itemPageLayout.complaintClaim.dealer.text"
          message="Normally, a 5-year right of complaint applies against hidden faults and defects from handover of the product. The right to complain is used if the product is in a worse condition than you had reason to expect, based on the information you have received from the seller. The right to complain may be limited if the dealer has a place of business outside Norway."
          comment="Dealer claim text"
        />
      </p>
      {complaintsUrl && (
        <p className="mt-16 mb-0">
          <a
            target="_blank"
            rel="noopener nofollow"
            href={complaintsUrl}
            onClick={() =>
              // Tracking event: MO_ev20
              trackClick({
                name: "Read about claim rights",
                objectId: "MO_ev20",
                elementType: ElementType.Link,
                intent: Intent.Open,
                objectExtras: { listingType: "professional" },
                target: {
                  id: complaintsUrl,
                  type: TargetType.Article,
                  name: "Reklamasjonsrett ved kjøp og salg av bil",
                },
              })
            }
          >
            <Trans
              id="itemPageLayout.complaintClaim.dealer.readMore"
              message="Read more about the right to complain at FINN's help centre."
              comment="Dealer claim read more link"
            />
          </a>
        </p>
      )}
    </div>
  );
};

export default ComplaintClaim;
